<template>
  <div class="summary-report">
    <h1 v-if="origin === 'standalone'">
      {{ $t('reportSummary.headline') }}
    </h1>

    <p v-if="origin === 'standalone'">
      {{ $t('reportSummary.introSeeFull') }}
    </p>

    <h2>{{ $t('reportSummary.introAnswers') }}</h2>

    <div class="advice-container consideration-container">
      <div v-if="showIcons" class="icon">
        <no-drag-image src="@/assets/icon-consideration.png" />
      </div>
      <!-- <h2 v-if="showHeadlines">
        {{ considerationHeadline }}
      </h2> -->
      <p>{{ considerationAdvice }}</p>
    </div>

    <h2>{{ $t('reportSummary.adviceIntro') }}</h2>

    <div v-if="highRiskIndicator" class="advice-container high-risk-container">
      <div v-if="showIcons" class="icon">
        <no-drag-image src="@/assets/icon-risk.png" />
      </div>
      <h3 v-if="showHeadlines">
        {{ $t('reportSummary.highRiskTriggerHeadline') }}
      </h3>
      <p>{{ highRiskTriggerText }}</p>
      <p v-if="showNotes" class="note">
        {{ $t('reportSummary.highRiskTriggerNote') }}
      </p>
    </div>

    <div v-if="bddIndicator" class="advice-container bdd-container">
      <div v-if="showIcons" class="icon">
        <no-drag-image src="@/assets/icon-bdd.png" />
      </div>
      <h3 v-if="showHeadlines">
        {{ $t('reportSummary.bddTriggerHeadline') }}
      </h3>
      <p>{{ bddTriggerText }}</p>
      <p v-if="showNotes" class="note">
        {{ $t('reportSummary.bddTriggerNote') }}
      </p>
    </div>

    <div v-if="impulsivityIndicator" class="advice-container impulsivity-container">
      <div v-if="showIcons" class="icon">
        <no-drag-image src="@/assets/icon-impulsivity.png" />
      </div>
      <h3 v-if="showHeadlines">
        {{ $t('reportSummary.impulsivityTriggerHeadline') }}
      </h3>
      <p>{{ impulsivityTriggerText }}</p>
    </div>

    <div v-if="advices.length > 0" class="other-advice-container">
      <p>{{ otherAdviceText }}</p>
      <div v-for="adviceHeadline in adviceHeadlines" :key="adviceHeadline.headline" class="advice-head">
        <h3>{{ adviceHeadline.headline }}</h3>

        <div v-for="advice in adviceHeadline.advice" :key="advice.id" class="other-advice">
          <p>{{ advice.advice }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CONSIDERATION_LEVEL_GREEN, CONSIDERATION_LEVEL_RED, CONSIDERATION_LEVEL_YELLOW } from '@/enum/ConsiderationLevel';
import NoDragImage from '@/components/common/NoDragImage';
import MarkdownText from '@/components/common/MarkdownText';

export default {
  name: 'SummaryReport',
  components: { MarkdownText, NoDragImage },
  props: {
    considerationLevel: {
      type: String,
      required: true,
    },
    highRiskIndicator: {
      type: Boolean,
      required: true,
    },
    bddIndicator: {
      type: Boolean,
      required: true,
    },
    impulsivityIndicator: {
      type: Boolean,
      required: true,
    },
    advices: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    origin: {
      // standalone or fullReport
      type: String,
      required: false,
      default: 'standalone',
    },
  },
  data() {
    return {
      adviceHeadlines: [],
    };
  },
  computed: {
    bddTriggerText() {
      let text = this.$t('reportSummary.bddTriggerText');

      if (this.origin === 'standalone') {
        text += ` ${this.$t('reportSummary.bddTriggerTextEndSummary')}`;
      }

      return text;
    },
    impulsivityTriggerText() {
      let text = this.$t('reportSummary.impulsivityTriggerText');

      if (this.origin === 'standalone') {
        text += ` ${this.$t('reportSummary.impulsivityTriggerTextEndSummary')}`;
      }

      return text;
    },
    otherAdviceText() {
      let text = this.$t('reportSummary.otherAdvice');

      if (this.origin === 'standalone') {
        text += ` ${this.$t('reportSummary.seeFullReportForFurtherInformation')}`;
      }

      return text;
    },
    highRiskTriggerText() {
      let text = this.$t('reportSummary.highRiskTriggerText');

      if (this.origin === 'standalone') {
        text += ` ${this.$t('reportSummary.seeFullReportForFurtherInformation')}`;
      }

      return text;
    },
    considerationLevelString() {
      if (this.considerationLevel === CONSIDERATION_LEVEL_GREEN) {
        return 'Green';
      }

      if (this.considerationLevel === CONSIDERATION_LEVEL_YELLOW) {
        return 'Yellow';
      }

      if (this.considerationLevel === CONSIDERATION_LEVEL_RED) {
        return 'Red';
      }

      return 'Unknown';
    },
    considerationHeadline() {
      return this.$t(`reportSummary.consideration${this.considerationLevelString}Headline`);
    },
    considerationAdvice() {
      let text = this.$t(`reportSummary.consideration${this.considerationLevelString}Text`);

      let endKey;

      if (this.origin === 'standalone') {
        endKey = `reportSummary.consideration${this.considerationLevelString}TextSummaryEnd`;
      } else if (this.origin === 'fullReport') {
        // endKey =`reportSummary.consideration${this.considerationLevelString}TextEndFull`;
      }

      if (endKey) {
        const endText = this.$t(endKey);

        if (endText && endText !== endKey) {
          text += ` ${endText}`;
        }
      }

      return text;
    },
    showPretty() {
      return true; // Client doesn't want this - enables notes, headlines and icons
    },
    showIcons() {
      return false;

      return this.origin === 'standalone' && this.showPretty;
    },
    showHeadlines() {
      return this.origin === 'standalone' && this.showPretty;
    },
    showNotes() {
      return false; // For now they've "hardcoded" this text into the summary text
      return this.showPretty;
    },
  },
  mounted() {
    this.recalculateAdviceHeadlines();
  },
  methods: {
    recalculateAdviceHeadlines() {
      const adviceHeadlines = {};

      for (const advice of this.advices) {
        if (!Object.keys(adviceHeadlines).includes(advice.headline)) {
          adviceHeadlines[advice.headline] = {
            headline: advice.headline,
            advice: [],
          };
        }

        adviceHeadlines[advice.headline].advice.push({
          advice: advice.advice,
          id: advice.id,
        });
      }

      this.adviceHeadlines = Object.values(adviceHeadlines);
    },
  },
};
</script>

<style lang="scss" scoped>
.summary-report {
  color: #063C5A;
  padding-bottom: 50px;
}

h1, h2 {
  font-weight: 700;
}

.icon {
  text-align: center;

  img {
    max-height: 50px;
  }
}

p {
  font-size: 12pt;
}

.advice-container {
  margin-bottom: 10px;
}

.high-risk-container {
  //background: rgba(228, 82, 72, 10%);
  //padding: 10px;
}

.note {
  padding-left: 25px;
  background-image: url("~@/assets/icon-note.png");
  background-repeat: no-repeat;
  background-position: top left;
  background-size: 20px;
  min-height: 35px;
  color: #557F97;
  font-weight: 600;
  font-size: 12pt;
  margin-bottom: 0;
}

.advice-head {
  &:last-child {
    hr {
      display: none;
    }
  }
}

.text-underline {
  text-decoration: underline;
}
</style>
